import React, { useState } from "react";
import axios from "axios";
import { API } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Truck, Plus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Section, Empty } from "@/components/services/Shared";

const EMPTY = { name: "", contact_name: "", phone: "", email: "", address: "", notes: "" };

export function SuppliersTab({ suppliers, reload, canDelete }) {
  const [editing, setEditing] = useState(null); // null | {} (new) | supplier
  const [form, setForm] = useState(EMPTY);
  const open = (s) => { setEditing(s || {}); setForm(s ? { ...EMPTY, ...s } : EMPTY); };
  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing.id) await axios.patch(`${API}/services/pharmacie/suppliers/${editing.id}`, form);
      else await axios.post(`${API}/services/pharmacie/suppliers`, form);
      toast.success(editing.id ? "Fournisseur mis à jour" : "Fournisseur ajouté"); setEditing(null); reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  const remove = async (s) => {
    if (!window.confirm(`Supprimer le fournisseur ${s.name} ?`)) return;
    try { await axios.delete(`${API}/services/pharmacie/suppliers/${s.id}`); toast.success("Supprimé"); reload(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  return (
    <Section title="Fournisseurs" icon={Truck} testid="suppliers-tab"
      action={<Button size="sm" className="bg-[#1E4631] text-white" onClick={() => open(null)} data-testid="supplier-new-btn"><Plus className="w-4 h-4 mr-1" /> Nouveau fournisseur</Button>}>
      {suppliers.length === 0 ? <Empty>Aucun fournisseur. Ajoutez le PNAM, la CDR ou vos grossistes.</Empty> : (
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase text-[#5C6B61]"><th className="py-2">Nom</th><th>Contact</th><th>Téléphone</th><th>Email</th><th>Commandes</th><th></th></tr></thead>
          <tbody className="divide-y divide-[#E5EBE7]">
            {suppliers.map((s) => (
              <tr key={s.id} data-testid={`supplier-${s.id}`}>
                <td className="py-2 font-medium">{s.name}{s.address && <div className="text-[11px] text-[#8A9990]">{s.address}</div>}</td>
                <td>{s.contact_name || "—"}</td><td>{s.phone || "—"}</td><td>{s.email || "—"}</td><td>{s.orders_count}</td>
                <td><div className="flex gap-1 justify-end">
                  <Button size="sm" variant="outline" onClick={() => open(s)} data-testid={`supplier-edit-${s.id}`}><Pencil className="w-4 h-4" /></Button>
                  {canDelete && <Button size="sm" variant="ghost" className="text-[#C62828]" onClick={() => remove(s)} data-testid={`supplier-delete-${s.id}`}><Trash2 className="w-4 h-4" /></Button>}
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing?.id ? "Modifier le fournisseur" : "Nouveau fournisseur"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3" data-testid="supplier-form">
            <div><Label>Nom</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="PNAM, CDR Kinshasa, Grossiste…" data-testid="supplier-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Personne de contact</Label><Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} data-testid="supplier-contact" /></div>
              <div><Label>Téléphone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+243…" data-testid="supplier-phone" /></div>
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="supplier-email" /></div>
              <div><Label>Adresse</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="supplier-address" /></div>
            </div>
            <div><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setEditing(null)}>Annuler</Button><Button type="submit" className="bg-[#1E4631] text-white" data-testid="supplier-save">Enregistrer</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Section>
  );
}
